<template>
  <div>
    <div class="header pt-2">
      <div class="d-flex justify-content-between">
        <span
          v-if="actionsCompleted"
          class="ap-title ap-title__main"
        >
          {{ $tc('diagnosis.batchActionsModal.finalPageTitle') }}
        </span>
        <span
          v-else
          class="ap-title ap-title__main"
        >
          {{ $tc('diagnosis.batchActionsModal.conflictsTitle') }}
        </span>
        <template v-if="isPreformatted">
          <b-button
            v-if="!actionsCompleted"
            id="pop-conflicts-help"
            qa="batchAction-help-button"
            class="secondary-button"
            variant="link"
          >
            <i
              class="far fa-question-circle mr-1"
              aria-hidden="true"
            />
            {{ $tc('global.help') }}
          </b-button>
          <b-popover
            placement="rightbottom"
            target="pop-conflicts-help"
            triggers="click blur"
          >
            <div v-if="preformattedAction.hasConflict">
              <div class="p-3">
                <div class="mb-2">
                  <span>{{ $tc('diagnosis.batchActionsModal.addInformationHelpTitle') }}</span>
                </div>
                <div class="mb-2">
                  <span><strong>{{ $tc('diagnosis.batchActionsModal.selectNoTitle') }}</strong></span><br>
                  <span>{{ $tc('diagnosis.batchActionsModal.selectNoHelp') }}</span>
                </div>
                <div class="mb-2">
                  <span><strong>{{ $tc('diagnosis.batchActionsModal.selectYesTitle') }}</strong></span><br>
                  <span>{{ $tc('diagnosis.batchActionsModal.selectYesHelp') }}</span>
                </div>
              </div>
            </div>
            <div v-else>
              <div class="p-3">
                <div class="mb-2">
                  <span><strong>{{ $tc('diagnosis.batchActionsModal.fullyReplace') }}</strong></span><br>
                  <span>{{ $tc('diagnosis.batchActionsModal.fullyReplaceHelp') }}</span>
                </div>
                <div class="mb-2">
                  <span><strong>{{ $tc('diagnosis.batchActionsModal.partiallyReplace') }}</strong></span><br>
                  <span>{{ $tc('diagnosis.batchActionsModal.partiallyReplaceHelp') }}</span>
                </div>
                <div class="mb-2">
                  <span><strong>{{ $tc('diagnosis.batchActionsModal.addInformation') }}</strong></span><br>
                  <span>{{ $tc('diagnosis.batchActionsModal.addInformationHelp') }}</span>
                </div>
              </div>
            </div>
          </b-popover>
        </template>
      </div>
    </div>

    <div
      v-if="!actionsCompleted"
      class="conflicts-area d-flex align-items-center justify-content-center"
    >
      <!-- Preformatted conflict: Sample has information -->
      <div
        v-if="visiblePreformattedConflictSampleHasInfo"
        class="text-center"
      >
        <i
          class="fal fa-exclamation-circle main fa-3x p-4"
          aria-hidden="true"
        />
        <div class="p-3">
          <h5 class="label-dark">
            {{ $tc('diagnosis.batchActionsModal.conflictsSample') }}
            <strong class="main">{{ currentElement.label }}</strong>
            {{ $tc('diagnosis.batchActionsModal.hasInformation') }}
          </h5>
          <h5 class="label-dark">
            <strong>{{ $tc('diagnosis.batchActionsModal.conflictQuestion') }}</strong>
          </h5>
        </div>
        <b-row class="mt-4">
          <b-col>
            <b-button
              qa="batchAction-fullyReplace"
              class="mr-2 btn__outline btn__outline--primary"
              block
              @click="processRestOfPreformattedElements(applyOptions.TOTAL_REPLACE)"
            >
              {{ $tc('diagnosis.batchActionsModal.fullyReplace') }}
            </b-button>
          </b-col>
          <b-col>
            <b-button
              qa="batchAction-partiallyReplace"
              class="mr-2 btn__outline btn__outline--primary"
              block
              @click="processRestOfPreformattedElements(applyOptions.PARTIAL_REPLACE)"
            >
              {{ $tc('diagnosis.batchActionsModal.partiallyReplace') }}
            </b-button>
          </b-col>
          <b-col>
            <b-button
              qa="batchAction-addInformation"
              class="mr-2 btn__outline btn__outline--primary"
              block
              @click="processRestOfPreformattedElements(applyOptions.ADD)"
            >
              {{ $tc('diagnosis.batchActionsModal.addInformation') }}
            </b-button>
          </b-col>
        </b-row>
        <div class="mt-5 d-flex align-items-center justify-content-center">
          <div class="switch-btn mt-3 p-0 d-flex align-items-center">
            <input
              id="togglePreformattedSampleHasInfoConflict"
              v-model="preformattedAction.applyToAll.sampleHasInfoConflict"
              class="offscreen"
              type="checkbox"
              aria-hidden="true"
            >
            <label
              for="togglePreformattedSampleHasInfoConflict"
              class="switch mb-0"
            />
            <span class="ml-3 dark">{{ $tc('diagnosis.batchActionsModal.applyToAllElements') }}</span>
          </div>
        </div>
      </div>
      <!-- Preformatted conflict: Sample has no edit information -->
      <div
        v-if="visiblePreformattedConflictSampleHasNoEditInfo"
        class="text-center"
      >
        <i
          class="fal fa-exclamation-circle main fa-3x p-4"
          aria-hidden="true"
        />
        <div class="p-3">
          <h5 class="label-dark">
            {{ $tc('diagnosis.batchActionsModal.conflictsSample') }}
            <strong class="main">
              {{ currentElement.label }}</strong>
            {{ $tc('diagnosis.batchActionsModal.hasNoEditInformation') }}
            <strong>{{ $tc('diagnosis.batchActionsModal.conflictQuestionContinue') }}</strong>
          </h5>
        </div>
        <b-row class="mt-4 d-flex justify-content-center">
          <b-col cols="3">
            <b-button
              qa="batchAction-addInfo-no"
              class="mr-2 btn__outline btn__outline--primary"
              block
              @click="applyPreformattedConflict(false)"
            >
              {{ $tc('diagnosis.batchActionsModal.no') }}
            </b-button>
          </b-col>
          <b-col cols="3">
            <b-button
              qa="batchAction-addInfo-yes"
              class="mr-2 btn__outline btn__outline--primary"
              block
              @click="applyPreformattedConflict(true)"
            >
              {{ $tc('diagnosis.batchActionsModal.yes') }}
            </b-button>
          </b-col>
        </b-row>
        <div class="mt-5 d-flex align-items-center justify-content-center">
          <div class="switch-btn mt-3 p-0 d-flex align-items-center">
            <input
              id="togglePreformattedSampleHasNoEditInfoConflict"
              v-model="preformattedAction.applyToAll.sampleHasNoEditInfoConflict"
              class="offscreen"
              type="checkbox"
              aria-hidden="true"
            >
            <label
              for="togglePreformattedSampleHasNoEditInfoConflict"
              class="switch mb-0"
            />
            <span class="ml-3 dark">{{ $tc('diagnosis.batchActionsModal.applyToAllElements') }}</span>
          </div>
        </div>
      </div>

      <!-- Template case conflict -->
      <div
        v-if="visibleTemplateConflictCase"
        class="text-center"
      >
        <i
          class="fal fa-exclamation-circle main fa-3x p-4"
          aria-hidden="true"
        />
        <div class="p-3">
          <h5 class="label-dark">
            {{ $tc('diagnosis.batchActionsModal.conflictsCase', 1, { caseNumber: currentElement.caseNumber }) }}
          </h5>
        </div>
        <b-row class="mt-4 d-flex justify-content-center">
          <b-col cols="3">
            <b-button
              qa="batchAction-template-no"
              class="mr-2 btn__outline btn__outline--primary"
              block
              @click="nextTemplateElement(false)"
            >
              {{ $tc('diagnosis.batchActionsModal.no') }}
            </b-button>
          </b-col>
          <b-col cols="3">
            <b-button
              qa="batchAction-template-yes"
              class="mr-2 btn__outline btn__outline--primary"
              block
              @click="nextTemplateElement(true)"
            >
              {{ $tc('diagnosis.batchActionsModal.yes') }}
            </b-button>
          </b-col>
        </b-row>
        <div class="mt-5 d-flex align-items-center justify-content-center">
          <div class="switch-btn mt-3 p-0 d-flex align-items-center">
            <input
              id="toggleTemplateCaseConflict"
              v-model="templateAction.applyToAll.caseConflict"
              class="offscreen"
              type="checkbox"
              aria-hidden="true"
            >
            <label
              for="toggleTemplateCaseConflict"
              class="switch mb-0"
            />
            <span class="ml-3 dark">{{ $tc('diagnosis.batchActionsModal.applyToAllElements') }}</span>
          </div>
        </div>
      </div>
      <!-- Synoptic template conflict -->
      <div
        v-if="visibleSynopticTemplateConflict"
        class="text-center"
      >
        <i
          class="fal fa-exclamation-circle main fa-3x p-4"
          aria-hidden="true"
        />
        <div class="p-3">
          <h5 class="label-dark">
            <strong class="main">{{ currentElement.caseNumber }}</strong> -
            {{ $tc('global.codificationsHaveChangedAfterApplyingSynopticTemplate') }}
          </h5>
        </div>
        <b-row class="mt-4 d-flex justify-content-center">
          <b-col cols="3">
            <b-button
              qa="batchAction-template-no"
              class="mr-2 btn__outline btn__outline--primary"
              block
              @click="applySynopticTemplateConflict(false)"
            >
              {{ $tc('diagnosis.batchActionsModal.add') }}
            </b-button>
          </b-col>
          <b-col cols="3">
            <b-button
              qa="batchAction-template-yes"
              class="mr-2 btn__outline btn__outline--primary"
              block
              @click="applySynopticTemplateConflict(true)"
            >
              {{ $tc('diagnosis.batchActionsModal.replace') }}
            </b-button>
          </b-col>
        </b-row>
        <div class="mt-5 d-flex align-items-center justify-content-center">
          <div class="switch-btn mt-3 p-0 d-flex align-items-center">
            <input
              id="toggleSynopticTemplateConflict"
              v-model="templateAction.applyToAll.synopticConflict"
              class="offscreen"
              type="checkbox"
              aria-hidden="true"
            >
            <label
              for="toggleSynopticTemplateConflict"
              class="switch mb-0"
            />
            <span class="ml-3 dark">{{ $tc('diagnosis.batchActionsModal.applyToAllElements') }}</span>
          </div>
        </div>
      </div>
    </div>
    <template v-else>
      <div
        v-if="updateErrors.length"
        class="table-area"
      >
        <b-row>
          <b-col>
            <div class="alert-card d-flex align-items-center py-2">
              <div>
                <i
                  class="fas fa-exclamation-triangle"
                  aria-hidden="true"
                />
              </div>
              <div style="line-height: 14px">
                <h3>
                  {{ finalPageWarningMessage }}
                </h3>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Table header section -->
        <b-row class="mt-4 mb-3">
          <b-col cols="2">
            <strong>{{ $tc('diagnosis.batchActionsModal.elementColumn') }}</strong>
          </b-col>
          <b-col cols="3">
            <strong>{{ $tc('diagnosis.batchActionsModal.action') }}</strong>
          </b-col>
          <b-col>
            <strong>{{ $tc('diagnosis.batchActionsModal.reason') }}</strong>
          </b-col>
        </b-row>
        <b-row
          v-for="(error, indexError) in updateErrors"
          :key="indexError"
          class="table-row py-2 d-flex align-items-center"
        >
          <b-col cols="2">
            {{ error.label }}
          </b-col>
          <b-col cols="3">
            {{ error.type }}
          </b-col>
          <b-col>
            {{ error.errorMessage }}
          </b-col>
        </b-row>
      </div>
      <div
        v-else
        class="conflicts-area d-flex align-items-center justify-content-center"
      >
        <div class="text-center">
          <i
            class="fal fa-check-circle success fa-3x p-4"
            aria-hidden="true"
          />
          <div class="p-3">
            <h5 class="label-dark">
              {{ $tc('diagnosis.batchActionsModal.finalPageSuccess') }}
            </h5>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import caseComprehensiveDiagnosisServices from '@/api/resources/cases/caseComprehensiveDiagnosisServices'
import { uniqBy } from 'lodash'
import { preformattedServices } from '@/api/resources/common/preformatted/preformatted.service'
import DiagnosisCaseService from '@/modules/diagnosisModule/services/DiagnosisCaseService'
import DiagnosisSampleService from '@/modules/diagnosisModule/services/DiagnosisSampleService'
import axios from 'axios'
import { StoreUtils } from '@/shared/util/store-utils'
import {
    DiagnosisBatchActionsApplyOption,
    DiagnosisBatchActionType
} from '@/modules/diagnosisModule/components/BatchActionsModal/batch-actions.model'
import apiExceptionType from '@/utils/apiExceptionType'

export default {
    name: 'ApplyActionsView',
    props: {
        selectedElements: { type: Array },
        actionTypeSelected: { type: String },
        templateSelected: { type: Object },
        preformattedIdSelected: { type: Number },
        preformattedData: { type: Object },
        updateCaseData: { type: Object }
    },
    data() {
        return {
            preformattedAction: {
                canApplyAction: false,
                applyOption: null,
                hasConflict: false,
                potentialConflictDetected: false,
                applyToAll: {
                    sampleHasNoEditInfoConflict: false,
                    sampleHasInfoConflict: false
                }
            },
            templateAction: {
                canApplyAction: false,
                replace: false,
                hasConflict: false,
                applyToAll: {
                    synopticConflict: false,
                    caseConflict: false
                }
            },
            indexElement: 0,
            actionsCompleted: false,
            updateErrors: []
        }
    },
    computed: {
        finalPageWarningMessage() {
            const elementsWithError = uniqBy(this.updateErrors, 'label')
            return this.$tc('diagnosis.batchActionsModal.finalPageWarning', 1, { elementsWithError: elementsWithError.length, totalElements: this.selectedElements.length })
        },
        laboratoryId() {
            return this.$store.getters.labId
        },
        applyOptions() {
            return DiagnosisBatchActionsApplyOption
        },

        isPreformatted() {
            return this.actionTypeSelected === DiagnosisBatchActionType.PREFORMATTED
        },
        isTextTemplate() {
            return this.actionTypeSelected === DiagnosisBatchActionType.TEXT_TEMPLATE
        },
        isSynopticTemplate() {
            return this.actionTypeSelected === DiagnosisBatchActionType.SYNOPTIC_TEMPLATE
        },
        isTemplate() {
            return this.isTextTemplate || this.isSynopticTemplate
        },

        sampleElements() {
            const sampleElements = []
            for (const element of this.selectedElements) {
                if (element.caseNumber) {
                    sampleElements.push(...element.samples)
                } else {
                    sampleElements.push(element)
                }
            }
            return uniqBy(sampleElements, 'id')
        },
        caseElements() {
            return uniqBy(
                this.selectedElements.map((element) => {
                    if (element.caseNumber) {
                        return element
                    }
                    return element.caseStudy
                }),
                'id')
        },
        currentElement() {
            if (this.actionTypeSelected === DiagnosisBatchActionType.PREFORMATTED) {
                if (this.indexElement < this.sampleElements.length) {
                    return this.sampleElements[this.indexElement]
                }
            }
            if (this.indexElement < this.caseElements.length) {
                return this.caseElements[this.indexElement]
            }
            return null
        },

        visibleTemplateConflictCase() {
            return this.currentElement && !!this.templateSelected && !this.templateAction.hasConflict && this.isTemplate
        },
        visibleSynopticTemplateConflict() {
            return this.currentElement && !!this.templateSelected && this.templateAction.hasConflict && this.isSynopticTemplate
        },
        visiblePreformattedConflictSampleHasNoEditInfo() {
            return this.currentElement && this.isPreformatted && this.preformattedAction.hasConflict
        },
        visiblePreformattedConflictSampleHasInfo() {
            return this.currentElement && !!this.isPreformatted && this.preformattedAction.potentialConflictDetected
        },

        canProcessElement() {
            if (this.visiblePreformattedConflictSampleHasInfo) {
                return this.preformattedAction.applyToAll.sampleHasInfoConflict
            }
            if (this.visiblePreformattedConflictSampleHasNoEditInfo) {
                return this.preformattedAction.applyToAll.sampleHasNoEditInfoConflict
            }

            if (this.visibleTemplateConflictCase) {
                return this.templateAction.applyToAll.caseConflict
            }
            if (this.visibleSynopticTemplateConflict) {
                return this.templateAction.applyToAll.synopticConflict
            }
            return false
        },

        canProcessCurrentElement() {
            return !!this.currentElement &&
                !this.preformattedAction.potentialConflictDetected &&
                !this.preformattedAction.hasConflict
        }
    },
    methods: {
        _handleApplyActionError(error, label, type) {
            if (axios.isAxiosError(error)) {
                this.updateErrors.push({
                    label,
                    type,
                    errorMessage: error.response.data.message
                })
            }
        },
        async _updateCases() {
            if (this.updateCaseData.pathologist || this.updateCaseData.administrative || this.updateCaseData.resident) {
                const request = {
                    administrativePerId: this.updateCaseData.administrative?.id,
                    comprehensiveDiagnosisObservations: this.updateCaseData.comprehensiveDiagnosisObservations,
                    pathologistPerId: this.updateCaseData.pathologist?.id,
                    priorityId: this.updateCaseData.priority?.id,
                    residentPerId: this.updateCaseData.resident?.id,
                    transcriptionRequired: this.updateCaseData.transcriptionRequired
                }
                for (const caseStudy of this.caseElements) {
                    request.originalConcurrencyUpdatedAt = caseStudy.originalConcurrencyUpdatedAt

                    await this.$_toCallSecure(
                        DiagnosisCaseService.updateCase(this.laboratoryId, caseStudy.id, request),
                        error =>
                            this._handleApplyActionError(
                                error,
                                caseStudy.caseNumber,
                                this.$tc('diagnosis.batchActionsModal.responsibles')))
                }
            }
        },
        async _markCasesAsInformed() {
            if (this.updateCaseData.markCasesAsInformed || this.updateCaseData.markCasesAsInformedAndTranscribed) {
                for (const caseStudy of this.caseElements) {
                    const response =
                        await this.$_toCallSecure(
                            DiagnosisCaseService.informCase(this.laboratoryId, caseStudy.id),
                            error =>
                                this._handleApplyActionError(
                                    error,
                                    caseStudy.caseNumber,
                                    this.$tc('diagnosis.batchActionsModal.status')))
                    if (response !== null && this.updateCaseData.markCasesAsInformedAndTranscribed) {
                        await this.$_toCallSecure(
                            DiagnosisCaseService.transcribeCase(this.laboratoryId, caseStudy.id),
                            error =>
                                this._handleApplyActionError(
                                    error,
                                    caseStudy.caseNumber,
                                    this.$tc('diagnosis.batchActionsModal.status')))
                    }
                }
            }
        },
        async _updateSamples() {
            if (this.updateCaseData.specialist) {
                const request = { specialistId: this.updateCaseData.specialist.id }
                for (const sample of this.sampleElements) {
                    request.originalConcurrencyUpdatedAt = sample.originalConcurrencyUpdatedAt

                    await this.$_toCallSecure(
                        DiagnosisSampleService.partiallyUpdateSample(
                            this.laboratoryId,
                            sample.caseStudy.id,
                            sample.id,
                            request),
                        error =>
                            this._handleApplyActionError(
                                error,
                                sample.label,
                                this.$tc('diagnosis.batchActionsModal.responsibles')))
                }
            }
        },
        async _processNextElement(elements, applyAction, errorCallback) {
            StoreUtils.updateLoading(true)
            try {
                if (this.currentElement) {
                    if (await applyAction()) {
                        this.indexElement++
                    } else {
                        await errorCallback()
                    }
                }

                if (this.canProcessElement) {
                    await this._processNextElement(elements, applyAction, errorCallback)
                } else if (this.indexElement === elements.length && !this.actionsCompleted) {
                    await this._updateCases()
                    await this._markCasesAsInformed()
                    await this._updateSamples()
                    this.actionsCompleted = true
                    this.$emit('actionsCompleted')
                }
            } finally {
                StoreUtils.updateLoading(false)
            }
        },
        /// PREFORMATTED
        async applyPreformattedConflict(canApplyAction) {
            this.preformattedAction.canApplyAction = canApplyAction

            if (canApplyAction) {
                await this.$_toCallSecure(
                    preformattedServices.applyPreformattedToSampleByAdd(
                        this.laboratoryId,
                        this.currentElement.caseStudy.id,
                        this.currentElement.id,
                        this.preformattedIdSelected,
                        false),
                    error =>
                        this._handleApplyActionError(
                            error,
                            this.currentElement.label,
                            this.$tc('diagnosis.batchActionsModal.preformated')))
            }

            this.preformattedAction.hasConflict = false
            this.indexElement++
            await this.processRestOfPreformattedElements()
        },
        async _applyPreformattedAction(applyOption) {
            let success = true

            let promise
            if (applyOption === DiagnosisBatchActionsApplyOption.ADD) {
                promise =
                    preformattedServices.applyPreformattedToSampleByAdd(
                        this.laboratoryId,
                        this.currentElement.caseStudy.id,
                        this.currentElement.id,
                        this.preformattedIdSelected,
                        true)
            } else {
                promise =
                    preformattedServices.applyPreformattedToSampleByReplace(
                        this.laboratoryId,
                        this.currentElement.caseStudy.id,
                        this.currentElement.id,
                        this.preformattedIdSelected,
                        applyOption === DiagnosisBatchActionsApplyOption.TOTAL_REPLACE)
            }

            await this.$_toCallSecure(
                promise,
                error => {
                    if (axios.isAxiosError(error)) {
                        if (error.response.data.code !== apiExceptionType.PREFORMATTED_CANNOT_ADD_SOME_SECTIONS) {
                            this._handleApplyActionError(
                                error,
                                this.currentElement.label,
                                this.$tc('diagnosis.batchActionsModal.preformated'))
                            return
                        }
                    }
                    success = false
                })
            return success
        },
        async isThereAnyPotentialConflictInCurrentElement() {
            const sampleSections = await this.$_toCallSecure(DiagnosisSampleService.sectionsSummary(
                this.currentElement.caseStudy.laboratory.labId,
                this.currentElement.caseStudy.id,
                this.currentElement.id
            ))
            return (!sampleSections ||
                !!(this.preformattedData.macro.text && sampleSections.macroHasData) ||
                !!(this.preformattedData.micro.text && sampleSections.microHasData) ||
                !!(this.preformattedData.diagnosis.text && sampleSections.diagnosisHasData) ||
                !!(this.preformattedData.additionalReport.text && sampleSections.additionalReportHasData) ||
                !!(this.preformattedData.codifications?.length && sampleSections.hasCodifications)
            )
        },
        async processRestOfPreformattedElements(applyOption) {
            try {
                StoreUtils.updateLoading(true)
                let shouldResolvePotentialConflict = !!applyOption || this.preformattedAction.applyToAll.sampleHasInfoConflict
                let currentApplyOption = applyOption ?? this.preformattedAction.applyOption ?? DiagnosisBatchActionsApplyOption.ADD
                this.preformattedAction.applyOption = currentApplyOption
                this.preformattedAction.potentialConflictDetected = false
                while (this.canProcessCurrentElement) {
                    const potentialConflictDetected = await this.isThereAnyPotentialConflictInCurrentElement()
                    if (!shouldResolvePotentialConflict) {
                        this.preformattedAction.potentialConflictDetected = potentialConflictDetected
                        if (potentialConflictDetected) {
                            continue
                        }
                    }
                    let actionSuccessful = await this._applyPreformattedAction(currentApplyOption)
                    if (!actionSuccessful) {
                        if (this.preformattedAction.applyToAll.sampleHasNoEditInfoConflict) {
                            await this.applyPreviousPreformattedConflictResolution()
                            this.indexElement++
                        } else {
                            this.preformattedAction.hasConflict = true
                        }
                    } else {
                        this.indexElement++
                    }
                    shouldResolvePotentialConflict = this.preformattedAction.applyToAll.sampleHasInfoConflict
                }

                if (this.indexElement >= this.selectedElements.length) {
                    await this._updateCases()
                    await this._markCasesAsInformed()
                    await this._updateSamples()
                    this.actionsCompleted = true
                    this.$emit('actionsCompleted')
                }
            } finally {
                StoreUtils.updateLoading(false)
            }
        },
        async applyPreviousPreformattedConflictResolution() {
            if (this.preformattedAction.canApplyAction) {
                await this.$_toCallSecure(
                    preformattedServices.applyPreformattedToSampleByAdd(
                        this.laboratoryId,
                        this.currentElement.caseStudy.id,
                        this.currentElement.id,
                        this.preformattedIdSelected,
                        false),
                    error =>
                        this._handleApplyActionError(
                            error,
                            this.currentElement.label,
                            this.$tc('diagnosis.batchActionsModal.preformated')))
            }
        },
        /// TEMPLATE
        async applySynopticTemplateConflict(replace) {
            this.templateAction.replace = replace

            this.templateSelected.originalConcurrencyUpdatedAt =
                this.currentElement.caseComprehensiveDiagnosisOriginalConcurrencyUpdatedAt

            await this.$_toCallSecure(
                caseComprehensiveDiagnosisServices.updateDescriptionAndSolveCodificationsChange(
                    this.laboratoryId,
                    this.currentElement.id,
                    this.templateSelected,
                    replace),
                error =>
                    this._handleApplyActionError(
                        error,
                        this.currentElement.caseNumber,
                        this.$tc('diagnosis.batchActionsModal.textTemplate')))

            this.templateAction.hasConflict = false
            this.indexElement++
            await this.nextTemplateElement(this.templateAction.canApplyAction)
        },
        async _applyTemplateAction(canApplyAction) {
            let success = true
            if (canApplyAction) {
                this.templateSelected.originalConcurrencyUpdatedAt =
                    this.currentElement.caseComprehensiveDiagnosisOriginalConcurrencyUpdatedAt

                await this.$_toCallSecure(
                    caseComprehensiveDiagnosisServices.updateDescription(
                        this.laboratoryId,
                        this.currentElement.id,
                        this.templateSelected),
                    error => {
                        if (axios.isAxiosError(error)) {
                            if (error.response.data.code !== apiExceptionType.ADD_OR_REPLACE_CODIFICATIONS_WHEN_APPLYING_SYNOPTIC_TEMPLATE_EXCEPTION) {
                                this._handleApplyActionError(
                                    error,
                                    this.currentElement.caseNumber,
                                    this.$tc('diagnosis.batchActionsModal.textTemplate'))
                                return
                            }
                        }
                        success = false
                    })
            }
            return success
        },
        async nextTemplateElement(canApplyAction) {
            this.templateAction.canApplyAction = canApplyAction

            await this._processNextElement(
                this.caseElements,
                () => this._applyTemplateAction(canApplyAction),
                async () => {
                    this.templateAction.hasConflict = true
                    if (this.templateAction.applyToAll.synopticConflict) {
                        await this.applySynopticTemplateConflict(this.templateAction.replace)
                        await this.nextTemplateElement(canApplyAction)
                    }
                })
        }
    }
}
</script>

<style scoped>

</style>
